import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash, Calendar as CalendarIcon, ArrowLeft, ArrowRight, ChartLine, Lightning, Warning, ThumbsUp } from "@phosphor-icons/react";
import {
  getDriProfiles, getMealLog, addToMealLog, deleteMealLogEntry, listRecipes,
  getMealLogWeek, getDeficitSuggestions, getCoach,
  NUTRIENT_LABELS, NUTRIENT_UNITS, MACRO_KEYS, VITAMIN_KEYS, MINERAL_KEYS, DIET_TAG_META,
} from "../lib/api";
import NutrientView from "../components/NutrientView";

function todayISO() { return new Date().toISOString().slice(0, 10); }
function shiftDate(iso, delta) {
  const d = new Date(iso); d.setDate(d.getDate() + delta); return d.toISOString().slice(0, 10);
}
function fmt(v, d = 1) {
  if (v == null || Number.isNaN(v)) return "0";
  const n = Number(v);
  if (Math.abs(n) >= 100) return n.toFixed(0);
  if (Math.abs(n) >= 10) return n.toFixed(1);
  return n.toFixed(d);
}

export default function DailyLog() {
  const [view, setView] = useState("day"); // day | week
  const [date, setDate] = useState(todayISO());
  const [profileKey, setProfileKey] = useState("adult_male_19_30");
  const [profiles, setProfiles] = useState([]);
  const [dietTags, setDietTags] = useState([]); // constraints for deficit fixer
  const [weekly, setWeekly] = useState(null);
  const [fixer, setFixer] = useState(null);
  const [fixerLoading, setFixerLoading] = useState(false);
  const [coach, setCoach] = useState(null);
  const [log, setLog] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [addingSrv, setAddingSrv] = useState(1);

  useEffect(() => {
    (async () => {
      try {
        const [p, r] = await Promise.all([getDriProfiles(), listRecipes()]);
        setProfiles(p.profiles || []); setRecipes(r.recipes || []);
      } catch (e) { toast.error("Failed to init"); }
    })();
  }, []);

  const load = useCallback(async () => {
    try { const l = await getMealLog(date, profileKey); setLog(l); }
    catch (e) { toast.error("Failed to load log"); }
  }, [date, profileKey]);

  useEffect(() => { load(); }, [load]);

  // Weekly aggregation
  useEffect(() => {
    if (view !== "week") return;
    (async () => {
      try {
        // start = Monday of the week containing `date`
        const d = new Date(date);
        const dow = (d.getDay() + 6) % 7; // 0 = Monday
        d.setDate(d.getDate() - dow);
        const start = d.toISOString().slice(0, 10);
        const w = await getMealLogWeek(start, profileKey);
        setWeekly(w);
      } catch (e) { toast.error("Failed to load week"); }
    })();
  }, [view, date, profileKey]);

  // Deficit fixer for the current day
  const loadFixer = useCallback(async () => {
    setFixerLoading(true);
    try {
      const s = await getDeficitSuggestions(date, profileKey, dietTags);
      setFixer(s);
    } catch (e) { /* noop */ }
    finally { setFixerLoading(false); }
  }, [date, profileKey, dietTags]);

  useEffect(() => { loadFixer(); }, [loadFixer]);

  useEffect(() => {
    (async () => {
      try { const c = await getCoach(date, profileKey); setCoach(c); }
      catch (e) { /* noop */ }
    })();
  }, [date, profileKey, log]);

  const add = async (recipeId) => {
    try {
      await addToMealLog(date, recipeId, Number(addingSrv) || 1);
      toast.success("Added to log");
      setPickerOpen(false); setAddingSrv(1); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    await deleteMealLogEntry(id); toast.success("Removed"); load();
  };

  const toggleDiet = (tag) => {
    setDietTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  const totals = log?.totals || {};
  const target = log?.profile?.values || {};
  const pcts = log?.percents || {};

  const kcalPct = Math.min(200, Number(pcts.calories || 0));

  return (
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-8 space-y-6" data-testid="daily-log-page">
      <div className="pb-6 border-b border-border flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Meal tracker</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-2">
            {view === "week" ? "Weekly summary" : "Daily log"}
          </h1>
          <p className="mt-3 text-muted-foreground max-w-2xl">
            {view === "week"
              ? "Roll-up of the last 7 days against your target profile. Chronically missed nutrients (below 80% on 3+ logged days) are highlighted."
              : "Add any saved recipe to a date. Totals accumulate against your daily targets so you can see coverage and deficits at a glance."}
          </p>
        </div>
        <div className="border border-border flex" data-testid="view-toggle">
          <button onClick={() => setView("day")} data-testid="view-day"
            className={`h-10 px-4 font-mono text-xs uppercase tracking-[0.15em] flex items-center gap-1.5 transition-colors duration-200 ${view === "day" ? "bg-foreground text-background" : "hover:bg-muted"}`}>
            <CalendarIcon size={14} /> Day
          </button>
          <button onClick={() => setView("week")} data-testid="view-week"
            className={`h-10 px-4 font-mono text-xs uppercase tracking-[0.15em] flex items-center gap-1.5 transition-colors duration-200 ${view === "week" ? "bg-foreground text-background" : "hover:bg-muted"}`}>
            <ChartLine size={14} /> Week
          </button>
        </div>
      </div>

      {view === "day" && <>
      {/* Nutrient Coach */}
      {coach && log?.entries?.length > 0 && (
        <div className="border border-border bg-card p-5" data-testid="coach-card">
          <div className="flex items-baseline justify-between mb-2">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground flex items-center gap-1.5">
              <Lightning size={12} weight="fill" className="text-primary" /> Nutrient coach
            </div>
            <div className="font-mono text-[10px] text-muted-foreground">{coach.profile?.label}</div>
          </div>
          <p className="font-display text-lg md:text-xl leading-snug" data-testid="coach-note">{coach.note}</p>
          {(coach.wins?.length > 0 || coach.fix) && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border">
              {coach.wins.map(w => (
                <div key={w.nutrient} className="bg-card p-3 flex items-center gap-2" data-testid={`coach-win-${w.nutrient}`}>
                  <ThumbsUp size={16} weight="fill" className="text-accent" />
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Nailed</div>
                    <div className="font-medium capitalize">{w.label} <span className="font-mono text-xs text-muted-foreground">{Math.round(w.pct)}%</span></div>
                  </div>
                </div>
              ))}
              {coach.fix && (
                <div className="bg-card p-3 flex items-center gap-2" data-testid={`coach-fix-${coach.fix.nutrient}`}>
                  <Warning size={16} weight="fill" className="text-primary" />
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Fix tomorrow</div>
                    <div className="font-medium capitalize">{coach.fix.label} <span className="font-mono text-xs text-muted-foreground">{Math.round(coach.fix.pct)}%</span></div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* controls */}
      <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_1fr_auto] gap-px bg-border border border-border" data-testid="log-controls">
        <div className="bg-card p-3 flex items-center gap-2">
          <button onClick={() => setDate(shiftDate(date, -1))} data-testid="date-prev"
            className="h-9 w-9 border border-border hover:bg-muted flex items-center justify-center transition-colors duration-200"><ArrowLeft size={14} /></button>
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
            data-testid="date-input"
            className="h-9 px-3 border border-border bg-background font-mono text-sm focus:outline-none focus:border-primary transition-colors duration-200" />
          <button onClick={() => setDate(shiftDate(date, 1))} data-testid="date-next"
            className="h-9 w-9 border border-border hover:bg-muted flex items-center justify-center transition-colors duration-200"><ArrowRight size={14} /></button>
        </div>
        <div className="bg-card p-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Daily profile</div>
          <select value={profileKey} onChange={e => setProfileKey(e.target.value)}
            data-testid="log-profile-select"
            className="w-full h-9 px-2 border border-border bg-background font-mono text-sm focus:outline-none focus:border-primary transition-colors duration-200">
            {profiles.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
        </div>
        <div className="bg-card p-3 flex items-center">
          <div className="font-mono text-xs text-muted-foreground">
            {log?.entries?.length || 0} meal{(log?.entries?.length || 0) === 1 ? "" : "s"} logged
          </div>
        </div>
        <div className="bg-card p-3 flex items-end">
          <button onClick={() => setPickerOpen(v => !v)} data-testid="add-meal-btn"
            className="w-full h-9 px-4 bg-primary text-primary-foreground font-mono text-xs uppercase tracking-[0.15em] hover:bg-primary/90 flex items-center justify-center gap-2 transition-colors duration-200">
            <Plus size={14} /> Add meal
          </button>
        </div>
      </div>

      {pickerOpen && (
        <div className="border border-border bg-card p-4 space-y-3" data-testid="recipe-picker">
          <div className="flex items-center justify-between">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Pick a recipe</div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Servings</span>
              <input type="number" min="0.25" step="0.25" value={addingSrv} onChange={e => setAddingSrv(e.target.value)}
                data-testid="add-servings"
                className="w-20 h-9 px-2 border border-border bg-background font-mono text-right focus:outline-none focus:border-primary transition-colors duration-200" />
            </div>
          </div>
          {recipes.length === 0 ? (
            <div className="p-4 text-muted-foreground text-sm">No recipes saved yet. <Link to="/" className="text-primary underline">Create one first</Link>.</div>
          ) : (
            <div className="grid gap-px bg-border max-h-[300px] overflow-y-auto">
              {recipes.map(r => (
                <button key={r.id} onClick={() => add(r.id)}
                  data-testid={`pick-${r.id}`}
                  className="grid grid-cols-[1fr_120px_80px] gap-4 px-3 py-2.5 bg-card hover:bg-muted text-left transition-colors duration-200">
                  <div>
                    <div className="font-medium truncate">{r.name}</div>
                    <div className="font-mono text-[10px] text-muted-foreground">{r.source_type} · {(r.diet_tags || []).slice(0, 3).join(" · ") || "—"}</div>
                  </div>
                  <div className="font-mono text-sm text-right">{Math.round(r.per_serving?.calories || 0)} kcal</div>
                  <div className="font-mono text-xs text-muted-foreground text-right">P {fmt(r.per_serving?.protein)}g</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Headline: calories vs target */}
      <div className="grid grid-cols-1 md:grid-cols-4 border border-border">
        <div className="p-6 md:col-span-2 border-b md:border-b-0 md:border-r border-border bg-primary text-primary-foreground" data-testid="day-calories">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] opacity-80">Energy · {log?.profile?.label || ""}</div>
          <div className="mt-2 flex items-baseline gap-2">
            <div className="font-mono text-6xl font-medium tracking-tighter">{fmt(totals.calories, 0)}</div>
            <div className="font-mono opacity-80 text-sm">/ {fmt(target.calories, 0)} kcal</div>
          </div>
          <div className="h-1.5 bg-white/20 mt-4 overflow-hidden"><div className="h-full bg-white transition-all duration-500" style={{ width: `${Math.min(100, kcalPct)}%` }} /></div>
          <div className="font-mono text-xs uppercase tracking-[0.2em] opacity-80 mt-2">{kcalPct}% of daily target</div>
        </div>
        {[
          { key: "protein", label: "Protein" },
          { key: "fiber", label: "Fiber" },
        ].map(x => {
          const pct = Math.min(200, Number(pcts[x.key] || 0));
          return (
            <div key={x.key} className="p-6 border-t md:border-t-0 md:border-r border-border last:border-r-0" data-testid={`day-${x.key}`}>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{x.label}</div>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="font-mono text-3xl font-medium tracking-tight">{fmt(totals[x.key])}</span>
                <span className="font-mono text-xs text-muted-foreground">/ {fmt(target[x.key])} {NUTRIENT_UNITS[x.key]}</span>
              </div>
              <div className="h-1.5 bg-muted mt-4 overflow-hidden"><div className="h-full bg-primary transition-all duration-500" style={{ width: `${Math.min(100, pct)}%` }} /></div>
              <div className="font-mono text-[10px] text-muted-foreground mt-1">{pct}% of daily · deficit {fmt(Math.max(0, target[x.key] - totals[x.key]))} {NUTRIENT_UNITS[x.key]}</div>
            </div>
          );
        })}
      </div>

      {/* Entries list */}
      <div>
        <h2 className="font-display font-bold text-2xl tracking-tight mb-3">Entries · {date}</h2>
        {(!log?.entries || log.entries.length === 0) ? (
          <div className="border border-dashed border-border p-10 text-center" data-testid="empty-log">
            <CalendarIcon size={30} className="mx-auto text-muted-foreground" />
            <div className="font-display font-bold text-lg mt-3">No entries yet</div>
            <div className="text-muted-foreground text-sm mt-1">Use "Add meal" to log a recipe against this date.</div>
          </div>
        ) : (
          <div className="border border-border" data-testid="log-entries">
            <div className="grid grid-cols-[1fr_100px_100px_100px_40px] gap-4 px-4 py-3 border-b border-border bg-muted/50 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              <div>Recipe</div><div className="text-right">Servings</div><div className="text-right">Calories</div><div className="text-right">Protein</div><div />
            </div>
            {log.entries.map(e => (
              <div key={e.id} className="grid grid-cols-[1fr_100px_100px_100px_40px] gap-4 px-4 py-3 border-b border-border last:border-b-0 items-center hover:bg-muted/30 transition-colors duration-150" data-testid={`entry-${e.id}`}>
                <Link to={`/recipe/${e.recipe_id}`} className="min-w-0">
                  <div className="font-medium truncate">{e.recipe_name}</div>
                  <div className="font-mono text-[10px] text-muted-foreground">{new Date(e.created_at).toLocaleTimeString()}</div>
                </Link>
                <div className="text-right font-mono text-sm">×{e.servings_eaten}</div>
                <div className="text-right font-mono text-sm">{Math.round((e.per_serving?.calories || 0) * e.servings_eaten)}</div>
                <div className="text-right font-mono text-sm">{fmt((e.per_serving?.protein || 0) * e.servings_eaten)}g</div>
                <button onClick={() => remove(e.id)} data-testid={`remove-entry-${e.id}`}
                  className="h-8 w-8 border border-border hover:bg-destructive hover:text-destructive-foreground flex items-center justify-center transition-colors duration-200">
                  <Trash size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Nutrient coverage — dashboard/chart/table across the full 44-nutrient schema */}
      <div>
        <h2 className="font-display font-bold text-2xl tracking-tight mb-3">Nutrient coverage · vs {log?.profile?.label || "target"}</h2>
        <NutrientView
          nutrients={totals}
          dri={target}
          defaultTab="standard"
          defaultView="dashboard"
          testIdPrefix="log-nutrient-view"
        />
      </div>

      {/* Deficit Fixer */}
      <div data-testid="deficit-fixer">
        <div className="pb-3 flex flex-wrap items-end justify-between gap-2">
          <div>
            <h2 className="font-display font-bold text-2xl tracking-tight flex items-center gap-2">
              <Lightning size={22} weight="fill" className="text-primary" /> Deficit fixer
            </h2>
            <p className="text-sm text-muted-foreground mt-1">Ingredients that best close today's biggest gaps.</p>
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="fixer-diet-chips">
            {["vegan", "vegetarian", "gluten_free", "dairy_free"].map(t => (
              <button key={t} onClick={() => toggleDiet(t)} data-testid={`fixer-diet-${t}`}
                className={`h-7 px-3 border font-mono text-[10px] uppercase tracking-[0.15em] transition-colors duration-200 ${
                  dietTags.includes(t) ? "bg-accent text-accent-foreground border-accent" : "border-border hover:bg-muted"
                }`}>
                {(DIET_TAG_META[t]?.label) || t}
              </button>
            ))}
          </div>
        </div>
        {fixerLoading ? (
          <div className="border border-border p-6 font-mono text-sm text-muted-foreground">Computing suggestions…</div>
        ) : !fixer || !fixer.suggestions?.length ? (
          <div className="border border-dashed border-border p-6 text-center font-mono text-sm text-muted-foreground">
            No significant deficits — you're on track for today.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
            {fixer.suggestions.map(s => (
              <div key={s.nutrient} className="bg-card p-4" data-testid={`fixer-${s.nutrient}`}>
                <div className="flex items-baseline justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{NUTRIENT_LABELS[s.nutrient]}</span>
                  <span className="font-mono text-[10px] text-primary flex items-center gap-1"><Warning size={9} /> {s.pct_short}% short</span>
                </div>
                <div className="mt-1 font-mono text-lg">
                  {fmt(s.current)} / {fmt(s.target)} <span className="text-xs text-muted-foreground">{NUTRIENT_UNITS[s.nutrient]}</span>
                </div>
                <div className="mt-3 space-y-1.5">
                  {s.picks.map(p => (
                    <div key={p.name} className="flex items-center gap-2 text-sm">
                      <div className="h-2.5 w-2.5 shrink-0" style={{ background: p.color }} />
                      <span className="capitalize flex-1 truncate">{p.name}</span>
                      <span className="font-mono text-xs text-muted-foreground">
                        {p.grams_to_close != null ? `${p.grams_to_close}g` : "—"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </>}

      {view === "week" && weekly && (
        <div className="space-y-6" data-testid="week-view">
          {/* Headline */}
          <div className="grid grid-cols-1 md:grid-cols-4 border border-border">
            <div className="p-6 md:col-span-2 border-b md:border-b-0 md:border-r border-border bg-primary text-primary-foreground" data-testid="week-calories">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] opacity-80">Avg daily · {weekly.profile.label}</div>
              <div className="mt-2 flex items-baseline gap-2">
                <div className="font-mono text-6xl font-medium tracking-tighter">{fmt(weekly.avg_daily.calories, 0)}</div>
                <div className="font-mono opacity-80 text-sm">/ {fmt(weekly.profile.values.calories, 0)} kcal</div>
              </div>
              <div className="font-mono text-xs uppercase tracking-[0.2em] opacity-80 mt-3">
                {weekly.logged_days} of 7 days logged · week of {weekly.start}
              </div>
            </div>
            <div className="p-6 border-b md:border-b-0 md:border-r border-border" data-testid="week-protein">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Avg protein</div>
              <div className="mt-2 font-mono text-3xl font-medium tracking-tight">{fmt(weekly.avg_daily.protein)}</div>
              <div className="font-mono text-[10px] text-muted-foreground mt-1">/ {fmt(weekly.profile.values.protein)} g · {weekly.avg_pct.protein}%</div>
            </div>
            <div className="p-6" data-testid="week-fiber">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Avg fiber</div>
              <div className="mt-2 font-mono text-3xl font-medium tracking-tight">{fmt(weekly.avg_daily.fiber)}</div>
              <div className="font-mono text-[10px] text-muted-foreground mt-1">/ {fmt(weekly.profile.values.fiber)} g · {weekly.avg_pct.fiber}%</div>
            </div>
          </div>

          {/* Chronically missed */}
          <div>
            <h2 className="font-display font-bold text-2xl tracking-tight mb-3 flex items-center gap-2">
              <Warning size={22} className="text-primary" /> Chronically missed
            </h2>
            {weekly.chronically_missed.length === 0 ? (
              <div className="border border-dashed border-border p-6 text-center font-mono text-sm text-muted-foreground">Nothing chronically short — nice work.</div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-border border border-border" data-testid="chronic-list">
                {weekly.chronically_missed.map(k => (
                  <div key={k} className="bg-card p-4" data-testid={`chronic-${k}`}>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{NUTRIENT_LABELS[k]}</div>
                    <div className="mt-1 font-mono text-xl">{fmt(weekly.avg_daily[k])} <span className="text-xs text-muted-foreground">/ {fmt(weekly.profile.values[k])} {NUTRIENT_UNITS[k]}</span></div>
                    <div className="mt-2 font-mono text-[10px] text-primary">
                      Missed on {weekly.days_below_80[k]} of {weekly.logged_days} logged days
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Per-day heat table */}
          <div>
            <h2 className="font-display font-bold text-2xl tracking-tight mb-3">Per-day breakdown</h2>
            <div className="border border-border overflow-x-auto" data-testid="week-table">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Date</th>
                    <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">kcal</th>
                    <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Protein</th>
                    <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Carbs</th>
                    <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Fat</th>
                    <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Fiber</th>
                    <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Iron</th>
                    <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Vit C</th>
                  </tr>
                </thead>
                <tbody>
                  {weekly.dates.map(d => {
                    const t = weekly.per_day_totals[d] || {};
                    const empty = !Object.values(t).some(v => v > 0);
                    return (
                      <tr key={d} className={`border-b border-border last:border-b-0 ${empty ? "opacity-50" : ""}`}>
                        <td className="px-3 py-2 font-mono text-xs">{d}</td>
                        <td className="text-right px-3 py-2 font-mono text-xs">{fmt(t.calories, 0)}</td>
                        <td className="text-right px-3 py-2 font-mono text-xs">{fmt(t.protein)}</td>
                        <td className="text-right px-3 py-2 font-mono text-xs">{fmt(t.carbs)}</td>
                        <td className="text-right px-3 py-2 font-mono text-xs">{fmt(t.fat)}</td>
                        <td className="text-right px-3 py-2 font-mono text-xs">{fmt(t.fiber)}</td>
                        <td className="text-right px-3 py-2 font-mono text-xs">{fmt(t.iron)}</td>
                        <td className="text-right px-3 py-2 font-mono text-xs">{fmt(t.vitamin_c)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Weekly nutrient dashboard across the full 44-nutrient schema */}
          <div>
            <h3 className="font-display font-bold text-xl tracking-tight mb-3">Weekly average · full profile</h3>
            <NutrientView
              nutrients={weekly.avg_daily}
              dri={weekly.profile.values}
              defaultTab="standard"
              defaultView="dashboard"
              testIdPrefix="week-nutrient-view"
            />
          </div>
        </div>
      )}
    </div>
  );
}
